import {
  PropsWithChildren,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { AuthContext } from "../../context/AuthContext";
import { EXCHANGE_APP_URL } from "../../utils/env";
import { affiliateError } from "../../hooks/api/admin/useAffiliates";
import {
  affiliateAddress,
  useAffiliateDashboard,
  useAffiliateDashboardPayments,
} from "../../hooks/api/admin/useAffiliateDashboard";
import { Affiliates } from ".";
import { AffiliateDashboardView } from "./DashboardView";

const PageFrame = styled.main`
  height: calc(100vh - 110px);
  overflow: auto;
  background: #08080e;
  color: #fff;
  min-width: 0;
`;

// Admin navigation wraps and uses a shorter mobile header. Fill the actual
// remaining viewport rather than assuming every header is 110 pixels tall.
function Page({ children }: PropsWithChildren<{}>) {
  const ref = useRef<HTMLElement>(null);
  const [height, setHeight] = useState<number>();
  useLayoutEffect(() => {
    const update = () => {
      if (ref.current)
        setHeight(
          Math.max(
            0,
            window.innerHeight - ref.current.getBoundingClientRect().top
          )
        );
    };
    let frame = 0;
    const resized = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(update);
    Array.from(ref.current?.parentElement?.children || []).forEach(
      (element) => {
        if (element !== ref.current) observer?.observe(element);
      }
    );
    update();
    window.addEventListener("resize", resized);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", resized);
    };
  }, []);
  return (
    <PageFrame ref={ref} style={{ height }}>
      {children}
    </PageFrame>
  );
}
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid #23232d;
  font-size: 13px;
  a {
    color: #4bd7ed;
  }
  span {
    color: #a2a2ac;
  }
  code {
    overflow-wrap: anywhere;
  }
`;
const Notice = styled.div`
  padding: 32px 24px;
  line-height: 1.6;
  button {
    margin-top: 12px;
    padding: 10px 18px;
    cursor: pointer;
  }
`;

function AffiliateDashboardPage({ owner }: { owner: string }) {
  const dashboard = useAffiliateDashboard(owner);
  const payments = useAffiliateDashboardPayments(owner);
  let content = <Notice role="status">Loading affiliate dashboard…</Notice>;
  if (!dashboard.allowed) {
    content = (
      <Notice>
        Affiliate dashboards are unavailable in this environment or staff
        session.
      </Notice>
    );
  } else if (dashboard.error) {
    content = (
      <Notice role="alert">
        Unable to load this affiliate dashboard:{" "}
        {affiliateError(dashboard.error)}
        <br />
        <button type="button" onClick={() => dashboard.mutate()}>
          Retry dashboard
        </button>
      </Notice>
    );
  } else if (dashboard.data) {
    content = (
      <AffiliateDashboardView
        data={dashboard.data}
        payments={payments}
        now={dashboard.data.server_time}
        fillsLoading={dashboard.fillsLoading}
        fillsError={dashboard.fillsError}
        onLoadMoreFills={dashboard.loadMore}
        referralLink={(code) =>
          `${EXCHANGE_APP_URL()}/affiliates?code=${encodeURIComponent(code)}`
        }
      />
    );
  }
  return (
    <Page>
      <Toolbar>
        <Link to="/affiliates">← All affiliates</Link>
        <strong>Staff view · Read-only</strong>
        <span>
          {process.env.REACT_APP_ENVIRONMENT === "mainnet"
            ? "MAINNET"
            : "TESTNET"}
        </span>
        <code>{owner}</code>
        {dashboard.data?.admin_nickname && (
          <strong>{dashboard.data.admin_nickname}</strong>
        )}
      </Toolbar>
      {content}
    </Page>
  );
}

/** Uses the existing /affiliates route and its page permission, with no new auth exceptions. */
export function AffiliateAdminPage() {
  const [params] = useSearchParams();
  const { tokenId } = useContext(AuthContext);
  const requested = params.getAll("affiliate");
  if (!requested.length) return <Affiliates />;
  const owner = requested[0].toLowerCase();
  if (requested.length !== 1 || !affiliateAddress(owner))
    return (
      <Page>
        <Toolbar>
          <Link to="/affiliates">← All affiliates</Link>
        </Toolbar>
        <Notice role="alert">
          Choose a valid affiliate wallet from the affiliate list.
        </Notice>
      </Page>
    );
  return (
    <AffiliateDashboardPage
      key={`${process.env.REACT_APP_ENVIRONMENT}:${tokenId}:${owner}`}
      owner={owner}
    />
  );
}
